import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "./ConsolePortfolio.css";
import {
  MoreHorizontal,
  ChevronDown,
  FolderOpen,
  Terminal,
  Settings,
  FileJson,
  FileText,
  Mail,
  Link as GitBranch,
  RefreshCw,
  CheckCircle,
  Bell,
  ExternalLink,
  Code,
  Activity,
} from "lucide-react";
import { DiJavascript1 } from "react-icons/di";
import { FiMail, FiGithub, FiLinkedin } from "react-icons/fi";
import resumePdf from "../../Assets/AJOTHOMASSAM.pdf";
// import ChatWidget from "../ChatWidget/ChatWidget";

const typewriterStrings = ["SOFTWARE DEVELOPER", "OPEN SOURCE CONTRIBUTOR...","TECH ENTHUSIAST","AI ENTHUSIAST"];

const GITHUB_USERNAME = "ajosam";

const CONTRIB_LEVEL_COLORS = [
  "rgba(255, 255, 255, 0.04)",
  "rgba(88, 166, 255, 0.25)",
  "rgba(88, 166, 255, 0.5)",
  "rgba(88, 166, 255, 0.75)",
  "rgba(88, 166, 255, 1)",
];

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const ContributionGraph = () => {
  const [weeks, setWeeks] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(
      `https://github-contributions-api.jogruber.de/v4/${GITHUB_USERNAME}?y=last`,
    )
      .then((res) => {
        if (!res.ok) throw new Error("request failed");
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const days = data.contributions || [];
        if (!days.length) throw new Error("no data");

        const firstDay = new Date(`${days[0].date}T00:00:00Z`).getUTCDay();
        const padded = Array(firstDay).fill(null).concat(days);
        const grouped = [];
        for (let i = 0; i < padded.length; i += 7) {
          grouped.push(padded.slice(i, i + 7));
        }
        setWeeks(grouped);
        setTotal(
          data.total?.lastYear ?? days.reduce((sum, d) => sum + d.count, 0),
        );
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div
        className="ide-font-mono ide-text-comment"
        style={{ fontSize: "0.875rem" }}
      >
        # unable to fetch activity data —{" "}
        <a
          href={`https://github.com/${GITHUB_USERNAME}`}
          target="_blank"
          rel="noreferrer"
          className="ide-text-accent"
        >
          view on GitHub
        </a>
      </div>
    );
  }

  if (!weeks) {
    return (
      <div
        className="ide-font-mono ide-text-muted"
        style={{ fontSize: "0.875rem" }}
      >
        Loading activity...
      </div>
    );
  }

  let lastMonth = -1;

  return (
    <div className="ide-contrib-wrapper">
      <div className="ide-contrib-scroll">
        <div className="ide-contrib-grid">
          {weeks.map((week, wi) => {
            const firstReal = week.find(Boolean);
            const monthIndex = firstReal
              ? new Date(`${firstReal.date}T00:00:00Z`).getUTCMonth()
              : -1;
            const showLabel = firstReal && monthIndex !== lastMonth;
            if (showLabel) lastMonth = monthIndex;

            return (
              <div key={wi} className="ide-contrib-week">
                <div className="ide-contrib-month-label">
                  {showLabel ? MONTH_NAMES[monthIndex] : ""}
                </div>
                {week.map((day, di) => (
                  <div
                    key={di}
                    className="ide-contrib-day"
                    style={{
                      background: day
                        ? CONTRIB_LEVEL_COLORS[day.level]
                        : "transparent",
                    }}
                    title={
                      day
                        ? `${day.count} contribution${day.count === 1 ? "" : "s"} on ${day.date}`
                        : ""
                    }
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
      <div className="ide-contrib-scroll-hint ide-font-mono ide-text-comment">
        ← scroll for full year →
      </div>
      <div className="ide-contrib-footer ide-font-mono">
        <span className="ide-text-muted" style={{ fontSize: "0.75rem" }}>
          {total} contributions in the last year
        </span>
        <div
          className="ide-flex"
          style={{ alignItems: "center", gap: "0.375rem", fontSize: "0.75rem" }}
        >
          <span className="ide-text-muted">Less</span>
          {CONTRIB_LEVEL_COLORS.map((color, i) => (
            <div
              key={i}
              className="ide-contrib-day"
              style={{ background: color }}
            />
          ))}
          <span className="ide-text-muted">More</span>
        </div>
      </div>
    </div>
  );
};

const ConsoleTypewriter = () => {
  const [index, setIndex] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let timeout;
    const full = typewriterStrings[index];
    const typingSpeed = 100;
    const pause = 2000;

    if (!deleting && displayed.length < full.length) {
      timeout = setTimeout(
        () => setDisplayed(full.slice(0, displayed.length + 1)),
        typingSpeed,
      );
    } else if (!deleting && displayed.length === full.length) {
      timeout = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && displayed.length > 0) {
      timeout = setTimeout(
        () => setDisplayed(full.slice(0, displayed.length - 1)),
        typingSpeed / 2,
      );
    } else if (deleting && displayed.length === 0) {
      setDeleting(false);
      setIndex((i) => (i + 1) % typewriterStrings.length);
    }
    return () => clearTimeout(timeout);
  }, [displayed, deleting, index]);

  return (
    <span style={{ display: "inline-block", maxWidth: "100%" }}>
      <span className="ide-text-accent" style={{ whiteSpace: "nowrap" }}>
        {displayed}
      </span>
      <motion.span
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        style={{ marginLeft: 2, color: "var(--accent)", fontWeight: "bold" }}
      ></motion.span>
    </span>
  );
};

const ConsolePortfolio = () => {
  const [activeSection, setActiveSection] = useState("hero");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("portfolio.sh");

  // Contact Form Logic
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const token = "8068379538:AAHj5J4-1OsWDRjECaBBcsn2wVE9ozMI9CI";
    const chatId = "658339236";
    const message = `
        📩 New Contact Form Submission:
        Name: ${formData.name}
        Email: ${formData.email}
        Message: ${formData.message}
          `;
    await fetch(`https://api.telegram.org/bot${token}/sendMessage`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        chat_id: chatId,
        text: message,
      }),
    });

    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });

      setTimeout(() => setSubmitted(false), 5000);
    }, 1500);
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="console-portfolio">
      {/* Header / Title Bar */}
      <header className="ide-header">
        <div
          className="ide-flex"
          style={{ alignItems: "center", gap: "0.5rem" }}
        >
          <div className="ide-window-controls">
            <div className="dot close"></div>
            <div className="dot min"></div>
            <div className="dot max"></div>
          </div>
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "var(--text-muted)",
              marginLeft: "1rem",
            }}
          >
            Ajo Sam
          </span>
        </div>
      </header>

      <div className="ide-main-container">
        {/* Sidebar Explorer */}
        {isSidebarOpen && (
          <aside className="ide-sidebar">
            <div className="ide-sidebar-title">
              <span>Explorer</span>
              <MoreHorizontal
                size={14}
                style={{ cursor: "pointer" }}
                onClick={() => setIsSidebarOpen(false)}
                title="Close sidebar"
              />
            </div>
            <div
              className="ide-flex-1 ide-font-mono"
              style={{ overflowY: "auto", padding: "0.5rem 0" }}
            >
              <div className="ide-folder" style={{ fontWeight: "bold" }}>
                <ChevronDown size={14} />
                <span>PORTFOLIO</span>
              </div>
              <div className="ide-folder" style={{ paddingLeft: "1.5rem" }}>
                <ChevronDown size={14} />
                <FolderOpen size={14} color="var(--accent)" />
                <span>src</span>
              </div>

              <a
                href="#hero"
                onClick={() => scrollToSection("hero")}
                className={`ide-file-item ${activeSection === "hero" ? "ide-file-active" : ""}`}
              >
                <Terminal size={14} color="var(--syntax-keyword)" />
                <span>main.sh</span>
              </a>
              <a
                href="#about"
                onClick={() => scrollToSection("about")}
                className={`ide-file-item ${activeSection === "about" ? "ide-file-active" : ""}`}
              >
                <DiJavascript1 size={14} color="#4cd7f6" />
                <span>about.js</span>
              </a>
              <a
                href="#skills"
                onClick={() => scrollToSection("skills")}
                className={`ide-file-item ${activeSection === "skills" ? "ide-file-active" : ""}`}
              >
                <Settings size={14} color="#fb923c" />
                <span>dependencies.yml</span>
              </a>
              <a
                href="#projects"
                onClick={() => scrollToSection("projects")}
                className={`ide-file-item ${activeSection === "projects" ? "ide-file-active" : ""}`}
              >
                <FileJson size={14} color="#facc15" />
                <span>projects.json</span>
              </a>
              <a
                href="#contributions"
                onClick={() => scrollToSection("contributions")}
                className={`ide-file-item ${activeSection === "contributions" ? "ide-file-active" : ""}`}
              >
                <Activity size={14} color="#f472b6" />
                <span>activity.log</span>
              </a>
              <a
                href="#experience"
                onClick={() => scrollToSection("experience")}
                className={`ide-file-item ${activeSection === "experience" ? "ide-file-active" : ""}`}
              >
                <FileText size={14} color="#60a5fa" />
                <span>history.md</span>
              </a>
              <a
                href="#contact"
                onClick={() => scrollToSection("contact")}
                className={`ide-file-item ${activeSection === "contact" ? "ide-file-active" : ""}`}
              >
                <Mail size={14} color="#4ade80" />
                <span>contact.env</span>
              </a>
            </div>
            <div
              style={{
                padding: "1rem",
                borderTop: "1px solid var(--border-color)",
              }}
            >
              <div
                style={{
                  fontSize: "0.625rem",
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  marginBottom: "0.5rem",
                }}
              >
                Connected Nodes
              </div>
              <div className="ide-flex" style={{ gap: "0.75rem" }}>
                <a
                  href="mailto:ajosam75@gmail.com"
                  title="Email"
                  style={{
                    color: "var(--text-muted)",
                    display: "flex",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "var(--accent)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "var(--text-muted)")
                  }
                >
                  <FiMail size={16} />
                </a>
                <a
                  href="https://github.com/ajosam"
                  target="_blank"
                  rel="noreferrer"
                  title="GitHub"
                  style={{
                    color: "var(--text-muted)",
                    display: "flex",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "var(--accent)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "var(--text-muted)")
                  }
                >
                  <FiGithub size={16} />
                </a>
                <a
                  href="https://www.linkedin.com/in/ajo-sam-39b481147/"
                  target="_blank"
                  rel="noreferrer"
                  title="LinkedIn"
                  style={{
                    color: "var(--text-muted)",
                    display: "flex",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "var(--accent)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "var(--text-muted)")
                  }
                >
                  <FiLinkedin size={16} />
                </a>
              </div>
            </div>
          </aside>
        )}

        {/* Main Editor Pane */}
        <main
          className="ide-flex-1"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="ide-tabs-container">
            <div
              className={`ide-tab ide-font-mono ${activeTab === "portfolio.sh" ? "ide-tab-active" : ""}`}
              onClick={() => setActiveTab("portfolio.sh")}
              style={{ opacity: activeTab === "portfolio.sh" ? 1 : 0.7 }}
            >
              <Terminal size={14} color="var(--syntax-keyword)" />
              <span>portfolio.sh</span>
              {activeTab === "portfolio.sh" && (
                <span style={{ marginLeft: "auto", cursor: "pointer" }}>×</span>
              )}
            </div>
            <div
              className={`ide-tab ide-font-mono ${activeTab === "resume.pdf" ? "ide-tab-active" : ""}`}
              onClick={() => setActiveTab("resume.pdf")}
              style={{ opacity: activeTab === "resume.pdf" ? 1 : 0.7 }}
            >
              <FileText size={14} color="#60a5fa" />
              <span>resume.pdf</span>
              {activeTab === "resume.pdf" && (
                <span style={{ marginLeft: "auto", cursor: "pointer" }}>×</span>
              )}
            </div>
          </div>

          {activeTab === "portfolio.sh" ? (
            <div className="ide-editor-pane">
              {/* Hero Section */}
              <section
                id="hero"
                className="ide-section ide-font-mono"
                style={{ justifyContent: "flex-start", paddingTop: "6rem" }}
              >
                <div
                  className="ide-hero-content"
                  style={{
                    maxWidth: "42rem",
                    width: "100%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.5rem",
                  }}
                >
                  <div className="ide-text-comment">
                    # Initializing transmission...
                  </div>
                  <div className="ide-flex" style={{ gap: "0.5rem" }}>
                    <span style={{ color: "#4ade80" }}>➜</span>
                    <span style={{ color: "white" }}>whoami</span>
                  </div>
                  <h1 className="ide-hero-title">
                    <ConsoleTypewriter />
                  </h1>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    <div className="ide-flex" style={{ gap: "0.5rem" }}>
                      <span style={{ color: "#4ade80" }}>➜</span>
                      <span style={{ color: "white" }}>cat intro.txt</span>
                    </div>
                    <p className="ide-text-muted ide-intro-text">
                      Building scalable, high-performance web applications that
                      bridge the gap between complex engineering and human
                      experience.
                    </p>
                  </div>
                  <div className="ide-hero-buttons">
                    <a
                      href="#projects"
                      onClick={() => scrollToSection("projects")}
                      className="ide-button-outline"
                    >
                      ./view-projects.sh
                    </a>
                    <button
                      className="ide-button-secondary"
                      onClick={() => setActiveTab("resume.pdf")}
                    >
                      curl -O resume.pdf
                    </button>
                  </div>
                </div>
              </section>

              {/* About Section */}
              <section
                id="about"
                className="ide-section"
                style={{
                  borderTop: "1px solid var(--border-color)",
                  minHeight: "auto",
                  padding: "6rem 3rem",
                }}
              >
                <div
                  className="ide-flex ide-font-mono"
                  style={{ marginBottom: "3rem", overflow: "hidden" }}
                >
                  <div className="ide-line-numbers">
                    1<br />2<br />3<br />4<br />5<br />6
                  </div>
                  <div
                    className="ide-philosophy-code"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <span className="ide-text-keyword">export const</span>{" "}
                    <span className="ide-text-function">philosophy</span> ={" "}
                    {"{"}
                    <br />
                    &nbsp;&nbsp;<span className="ide-text-string">"focus"</span>
                    :{" "}
                    <span className="ide-text-string">
                      "Full Stack Development"
                    </span>
                    ,<br />
                    &nbsp;&nbsp;
                    <span className="ide-text-string">"experience"</span>:{" "}
                    <span style={{ color: "#fb923c" }}>6+</span>,<br />
                    &nbsp;&nbsp;
                    <span className="ide-text-string">"delivered"</span>:{" "}
                    <span style={{ color: "#fb923c" }}>10+</span>,<br />
                    &nbsp;&nbsp;<span className="ide-text-string">"quote"</span>
                    :{" "}
                    <span className="ide-text-string">
                      "Work hard in silence, let your success be your noise"
                    </span>
                    <br />
                    {"}"}
                  </div>
                </div>
                <div style={{ maxWidth: "90%", width: "100%" }}>
                  <h3
                    style={{
                      fontSize: "1.875rem",
                      fontWeight: "bold",
                      fontStyle: "italic",
                      marginBottom: "1.5rem",
                      color: "white",
                    }}
                  >
                    {"// About Me"}
                  </h3>
                  <p className="ide-text-muted ide-about-text">
                    Full-Stack Developer with 6+ years of experience building scalable and user-centric web applications using JavaScript, TypeScript, React.js, Next.js, Vue.js, and Node.js. I specialize in crafting intuitive, high-performance interfaces and transforming complex requirements into clean, maintainable solutions.I am passionate about building SaaS products, exploring AI-powered experiences, and continuously expanding my expertise in modern technologies, including Flutter and intelligent applications.
                  </p>
                </div>
              </section>

              {/* Skills Section */}
              <section
                id="skills"
                className="ide-section"
                style={{
                  borderTop: "1px solid var(--border-color)",
                  background: "#010409",
                  minHeight: "auto",
                  padding: "6rem 3rem",
                }}
              >
                <h3
                  className="ide-font-mono ide-text-keyword"
                  style={{
                    textTransform: "uppercase",
                    marginBottom: "3rem",
                    fontSize: "0.875rem",
                  }}
                >
                  dependencies.yml
                </h3>
                <div className="ide-skills-grid">
                  {/* Frontend */}
                  <div
                    className="ide-dependency-card ide-font-mono"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <div
                      className="ide-flex"
                      style={{
                        alignItems: "center",
                        gap: "0.75rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <Code size={18} color="#60a5fa" />
                      <span style={{ fontWeight: "bold", color: "white" }}>
                        frontend_dev:
                      </span>
                    </div>
                    <div
                      style={{
                        paddingLeft: "1.75rem",
                        borderLeft: "2px solid #30363d",
                        color: "var(--text-muted)",
                        lineHeight: "1.8",
                      }}
                    >
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> ReactJS,
                        VueJS
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> JavaScript,
                        TypeScript
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> HTML5, CSS,
                        Tailwind CSS
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Responsive &
                        Perf Optimized UI
                      </div>
                    </div>
                  </div>

                  {/* Backend */}
                  <div
                    className="ide-dependency-card ide-font-mono"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <div
                      className="ide-flex"
                      style={{
                        alignItems: "center",
                        gap: "0.75rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <Terminal size={18} color="#facc15" />
                      <span style={{ fontWeight: "bold", color: "white" }}>
                        backend_dev:
                      </span>
                    </div>
                    <div
                      style={{
                        paddingLeft: "1.75rem",
                        borderLeft: "2px solid #30363d",
                        color: "var(--text-muted)",
                        lineHeight: "1.8",
                      }}
                    >
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Node.js
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> PHP, Laravel
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> MySQL, SQL
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Firebase
                      </div>
                    </div>
                  </div>

                  {/* Mobile */}
                  <div
                    className="ide-dependency-card ide-font-mono"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <div
                      className="ide-flex"
                      style={{
                        alignItems: "center",
                        gap: "0.75rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <Settings size={18} color="#c084fc" />
                      <span style={{ fontWeight: "bold", color: "white" }}>
                        mobile_dev:
                      </span>
                    </div>
                    <div
                      style={{
                        paddingLeft: "1.75rem",
                        borderLeft: "2px solid #30363d",
                        color: "var(--text-muted)",
                        lineHeight: "1.8",
                      }}
                    >
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Flutter
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span>{" "}
                        Cross-platform Development
                      </div>
                    </div>
                  </div>

                  {/* Tools & DevOps */}
                  <div
                    className="ide-dependency-card ide-font-mono"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <div
                      className="ide-flex"
                      style={{
                        alignItems: "center",
                        gap: "0.75rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <GitBranch size={18} color="#fb923c" />
                      <span style={{ fontWeight: "bold", color: "white" }}>
                        tools_&_devops:
                      </span>
                    </div>
                    <div
                      style={{
                        paddingLeft: "1.75rem",
                        borderLeft: "2px solid #30363d",
                        color: "var(--text-muted)",
                        lineHeight: "1.8",
                      }}
                    >
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> GitHub,
                        Azure DevOps
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> CI/CD
                        Awareness
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> cPanel,
                        Google Apps Script
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Deployment
                        Management
                      </div>
                    </div>
                  </div>

                  {/* Architecture & Patterns */}
                  <div
                    className="ide-dependency-card ide-font-mono"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <div
                      className="ide-flex"
                      style={{
                        alignItems: "center",
                        gap: "0.75rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <FolderOpen size={18} color="#4ade80" />
                      <span style={{ fontWeight: "bold", color: "white" }}>
                        architecture:
                      </span>
                    </div>
                    <div
                      style={{
                        paddingLeft: "1.75rem",
                        borderLeft: "2px solid #30363d",
                        color: "var(--text-muted)",
                        lineHeight: "1.8",
                      }}
                    >
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> MVVM, MVC
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Reactive
                        Programming
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Declarative
                        UI
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> REST-based
                        System Design
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Scalable
                        Architectures
                      </div>
                    </div>
                  </div>

                  {/* Testing & Quality */}
                  <div
                    className="ide-dependency-card ide-font-mono"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <div
                      className="ide-flex"
                      style={{
                        alignItems: "center",
                        gap: "0.75rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <CheckCircle size={18} color="#f43f5e" />
                      <span style={{ fontWeight: "bold", color: "white" }}>
                        testing_&_quality:
                      </span>
                    </div>
                    <div
                      style={{
                        paddingLeft: "1.75rem",
                        borderLeft: "2px solid #30363d",
                        color: "var(--text-muted)",
                        lineHeight: "1.8",
                      }}
                    >
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Jest, Enzyme
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Unit Testing
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Code Reviews
                      </div>
                      <div>
                        <span style={{ color: "#4ade80" }}>-</span> Security &
                        Perf Impacts
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Projects Section */}
              <section
                id="projects"
                className="ide-section"
                style={{
                  borderTop: "1px solid var(--border-color)",
                  minHeight: "auto",
                  padding: "6rem 3rem",
                }}
              >
                <div
                  className="ide-flex"
                  style={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "4rem",
                  }}
                >
                  <h3
                    className="ide-font-mono"
                    style={{
                      fontSize: "1.875rem",
                      fontWeight: "bold",
                      color: "white",
                    }}
                  >
                    projects.json
                  </h3>
                  {/* <div className="ide-font-mono ide-text-muted" style={{ fontSize: '0.75rem' }}>count: 6 results</div> */}
                </div>
                <div className="ide-projects-grid">
                  {/* Proj 1 */}
                  <div className="ide-project-card ide-font-mono">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      <div>
                        <span className="ide-text-keyword">title:</span>{" "}
                        <span className="ide-text-string">"Prop Doc"</span>,
                      </div>
                      <div>
                        <span className="ide-text-keyword">stack:</span> [
                        <span className="ide-text-string">"React"</span>,{" "}
                        <span className="ide-text-string">"TypeScript"</span>],
                      </div>
                      <div>
                        <span className="ide-text-keyword">description:</span>{" "}
                        <span className="ide-text-string">
                          "A research assistant for Dubai off-plan real estate
                          that lets you track properties, compare net yields,
                          and chat with AI about brochures, SPAs, and payment
                          plans."
                        </span>
                        ,
                      </div>
                      <div
                        className="ide-flex"
                        style={{ gap: "1rem", marginTop: "1rem" }}
                      >
                        <a
                          href="https://prop-doc-gamma.vercel.app/library"
                          target="_blank"
                          rel="noreferrer"
                          className="ide-text-accent"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            textDecoration: "none",
                          }}
                        >
                          <ExternalLink size={14} /> view_project()
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Proj 2 */}
                  <div className="ide-project-card ide-font-mono">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      <div>
                        <span className="ide-text-keyword">title:</span>{" "}
                        <span className="ide-text-string">"NPM Package"</span>,
                      </div>
                      <div>
                        <span className="ide-text-keyword">stack:</span> [
                        <span className="ide-text-string">"React"</span>,{" "}
                        <span className="ide-text-string">"Hooks"</span>],
                      </div>
                      <div>
                        <span className="ide-text-keyword">description:</span>{" "}
                        <span className="ide-text-string">
                          "A lightweight React hook that helps easily detect
                          clicks outside tags."
                        </span>
                        ,
                      </div>
                      <div
                        className="ide-flex"
                        style={{ gap: "1rem", marginTop: "1rem" }}
                      >
                        <a
                          href="https://www.npmjs.com/package/use-click-outside-simple"
                          target="_blank"
                          rel="noreferrer"
                          className="ide-text-accent"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            textDecoration: "none",
                          }}
                        >
                          <ExternalLink size={14} /> view_project()
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Proj 3 */}
                  <div className="ide-project-card ide-font-mono">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      <div>
                        <span className="ide-text-keyword">title:</span>{" "}
                        <span className="ide-text-string">
                          "3D Lego Simulator"
                        </span>
                        ,
                      </div>
                      <div>
                        <span className="ide-text-keyword">stack:</span> [
                        <span className="ide-text-string">"React"</span>,{" "}
                        <span className="ide-text-string">
                          "React Three Fiber"
                        </span>
                        ],
                      </div>
                      <div>
                        <span className="ide-text-keyword">description:</span>{" "}
                        <span className="ide-text-string">
                          "Interactive 3D building experience built with React
                          Three Fiber."
                        </span>
                        ,
                      </div>
                      <div
                        className="ide-flex"
                        style={{ gap: "1rem", marginTop: "1rem" }}
                      >
                        <a
                          href="https://github.com/ajosam/lego"
                          target="_blank"
                          rel="noreferrer"
                          className="ide-text-accent"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            textDecoration: "none",
                          }}
                        >
                          <ExternalLink size={14} /> view_project()
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Proj 4 */}
                  <div className="ide-project-card ide-font-mono">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      <div>
                        <span className="ide-text-keyword">title:</span>{" "}
                        <span className="ide-text-string">
                          "Chat Messenger"
                        </span>
                        ,
                      </div>
                      <div>
                        <span className="ide-text-keyword">stack:</span> [
                        <span className="ide-text-string">"React"</span>,{" "}
                        <span className="ide-text-string">"Stream SDK"</span>],
                      </div>
                      <div>
                        <span className="ide-text-keyword">description:</span>{" "}
                        <span className="ide-text-string">
                          "Real-time chat messenger with video call support via
                          Stream SDK."
                        </span>
                        ,
                      </div>
                      <div
                        className="ide-flex"
                        style={{ gap: "1rem", marginTop: "1rem" }}
                      >
                        <a
                          href="https://github.com/ajosam/chat-messenger"
                          target="_blank"
                          rel="noreferrer"
                          className="ide-text-accent"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            textDecoration: "none",
                          }}
                        >
                          <ExternalLink size={14} /> view_project()
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Proj 5 */}
                  <div className="ide-project-card ide-font-mono">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      <div>
                        <span className="ide-text-keyword">title:</span>{" "}
                        <span className="ide-text-string">"Netflix Clone"</span>
                        ,
                      </div>
                      <div>
                        <span className="ide-text-keyword">stack:</span> [
                        <span className="ide-text-string">"React"</span>,{" "}
                        <span className="ide-text-string">"Redux"</span>],
                      </div>
                      <div>
                        <span className="ide-text-keyword">description:</span>{" "}
                        <span className="ide-text-string">
                          "Front-end clone of Netflix made using React and
                          Redux."
                        </span>
                        ,
                      </div>
                      <div
                        className="ide-flex"
                        style={{ gap: "1rem", marginTop: "1rem" }}
                      >
                        <a
                          href="https://github.com/ajosam/movie-app"
                          target="_blank"
                          rel="noreferrer"
                          className="ide-text-accent"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            textDecoration: "none",
                          }}
                        >
                          <ExternalLink size={14} /> view_project()
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Proj 6 */}
                  <div className="ide-project-card ide-font-mono">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      <div>
                        <span className="ide-text-keyword">title:</span>{" "}
                        <span className="ide-text-string">"Amazon Clone"</span>,
                      </div>
                      <div>
                        <span className="ide-text-keyword">stack:</span> [
                        <span className="ide-text-string">"React"</span>,{" "}
                        <span className="ide-text-string">"Redux"</span>],
                      </div>
                      <div>
                        <span className="ide-text-keyword">description:</span>{" "}
                        <span className="ide-text-string">
                          "Engineered a fully responsive Amazon clone with React
                          and Redux."
                        </span>
                        ,
                      </div>
                      <div
                        className="ide-flex"
                        style={{ gap: "1rem", marginTop: "1rem" }}
                      >
                        <a
                          href="https://github.com/ajosam/e_commerce"
                          target="_blank"
                          rel="noreferrer"
                          className="ide-text-accent"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            textDecoration: "none",
                          }}
                        >
                          <ExternalLink size={14} /> view_project()
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Proj 7 */}
                  <div className="ide-project-card ide-font-mono">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      <div>
                        <span className="ide-text-keyword">title:</span>{" "}
                        <span className="ide-text-string">"Olx Clone"</span>,
                      </div>
                      <div>
                        <span className="ide-text-keyword">stack:</span> [
                        <span className="ide-text-string">"React"</span>,{" "}
                        <span className="ide-text-string">"Redux"</span>],
                      </div>
                      <div>
                        <span className="ide-text-keyword">description:</span>{" "}
                        <span className="ide-text-string">
                          "Developed an OLX clone using React and Redux."
                        </span>
                        ,
                      </div>
                      <div
                        className="ide-flex"
                        style={{ gap: "1rem", marginTop: "1rem" }}
                      >
                        <a
                          href="https://github.com/ajosam/olx-clone"
                          target="_blank"
                          rel="noreferrer"
                          className="ide-text-accent"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            textDecoration: "none",
                          }}
                        >
                          <ExternalLink size={14} /> view_project()
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contributions Section */}
              <section
                id="contributions"
                className="ide-section"
                style={{
                  borderTop: "1px solid var(--border-color)",
                  minHeight: "auto",
                  padding: "6rem 3rem",
                }}
              >
                <div style={{ maxWidth: "72rem", width: "100%" }}>
                  <div
                    className="ide-flex"
                    style={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "2rem",
                      flexWrap: "wrap",
                      gap: "1rem",
                    }}
                  >
                    <h3
                      className="ide-font-mono"
                      style={{
                        fontSize: "1.875rem",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      activity.log
                    </h3>
                    <a
                      href={`https://github.com/${GITHUB_USERNAME}`}
                      target="_blank"
                      rel="noreferrer"
                      className="ide-text-accent ide-font-mono"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.375rem",
                        textDecoration: "none",
                        fontSize: "0.75rem",
                      }}
                    >
                      <ExternalLink size={14} /> view_full_history()
                    </a>
                  </div>
                  <div
                    className="ide-flex ide-font-mono ide-contrib-command"
                    style={{ gap: "0.5rem", marginBottom: "1.5rem" }}
                  >
                    <span style={{ color: "#4ade80" }}>➜</span>
                    <span style={{ color: "white" }}>
                      git log --since="1 year ago" --format=%cd --date=short |
                      uniq -c
                    </span>
                  </div>
                  <div className="ide-dependency-card ide-contrib-card">
                    <ContributionGraph />
                  </div>
                </div>
              </section>

              {/* Experience Section */}
              <section
                id="experience"
                className="ide-section"
                style={{
                  borderTop: "1px solid var(--border-color)",
                  background: "#0d1117",
                  minHeight: "auto",
                  padding: "6rem 3rem",
                }}
              >
                <div
                  style={{ maxWidth: "48rem", margin: "0 auto", width: "100%" }}
                >
                  <div
                    style={{
                      borderBottom: "1px solid var(--border-color)",
                      paddingBottom: "1rem",
                      marginBottom: "3rem",
                    }}
                  >
                    <h2
                      className="ide-font-mono"
                      style={{
                        fontSize: "2.25rem",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      # history.md
                    </h2>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4rem",
                    }}
                  >
                    <div
                      style={{
                        paddingLeft: "2rem",
                        borderLeft: "2px solid var(--border-color)",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: "-9px",
                          top: "0",
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          background: "var(--accent)",
                          border: "4px solid #0d1117",
                        }}
                      ></div>
                      <h3
                        style={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                          marginBottom: "0.5rem",
                          color: "white",
                        }}
                      >
                        ## Software Developer
                      </h3>
                      <div
                        className="ide-font-mono ide-text-accent"
                        style={{ fontSize: "0.875rem", marginBottom: "1rem" }}
                      >
                        Property Matters Software Design | 2023 Oct - PRESENT
                      </div>
                      <ul
                        className="ide-font-mono ide-text-muted"
                        style={{
                          fontSize: "0.875rem",
                          lineHeight: "1.6",
                          listStyle: "none",
                          padding: 0,
                        }}
                      >
                        📍 Dubai
                      </ul>
                    </div>
                    <div
                      style={{
                        paddingLeft: "2rem",
                        borderLeft: "2px solid var(--border-color)",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: "-9px",
                          top: "0",
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          background: "var(--accent)",
                          border: "4px solid #0d1117",
                        }}
                      ></div>
                      <h3
                        style={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                          marginBottom: "0.5rem",
                          color: "white",
                        }}
                      >
                        ## Frontend Developer
                      </h3>
                      <div
                        className="ide-font-mono ide-text-accent"
                        style={{ fontSize: "0.875rem", marginBottom: "1rem" }}
                      >
                        Infosys | 2021 Dec - 2023 Aug
                      </div>
                      <p
                        className="ide-font-mono ide-text-muted"
                        style={{ fontSize: "0.875rem" }}
                      >
                        📍 Bengaluru
                      </p>
                    </div>
                    <div
                      style={{
                        paddingLeft: "2rem",
                        borderLeft: "2px solid var(--border-color)",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: "-9px",
                          top: "0",
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          background: "var(--accent)",
                          border: "4px solid #0d1117",
                        }}
                      ></div>
                      <h3
                        style={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                          marginBottom: "0.5rem",
                          color: "white",
                        }}
                      >
                        ## Web Developer Executive
                      </h3>
                      <div
                        className="ide-font-mono ide-text-accent"
                        style={{ fontSize: "0.875rem", marginBottom: "1rem" }}
                      >
                        Econz IT Services | 2020 Apr - 2021 Nov
                      </div>
                      <p
                        className="ide-font-mono ide-text-muted"
                        style={{ fontSize: "0.875rem" }}
                      >
                        📍 Bengaluru
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contact Section */}
              <section
                id="contact"
                className="ide-section"
                style={{
                  borderTop: "1px solid var(--border-color)",
                  background: "#010409",
                  minHeight: "auto",
                  padding: "6rem 3rem",
                }}
              >
                <div
                  style={{ maxWidth: "42rem", margin: "0 auto", width: "100%" }}
                >
                  <div style={{ marginBottom: "3rem" }}>
                    <h3
                      className="ide-font-mono"
                      style={{
                        fontSize: "1.875rem",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      contact.env
                    </h3>
                    <p
                      className="ide-text-comment ide-font-mono"
                      style={{
                        marginTop: "0.5rem",
                        fontStyle: "italic",
                        fontSize: "0.875rem",
                      }}
                    >
                      # Send a message to the control center
                    </p>
                  </div>
                  {!submitted ? (
                    <form
                      onSubmit={handleFormSubmit}
                      className="ide-font-mono"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2rem",
                      }}
                    >
                      <div className="ide-contact-grid">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.5rem",
                          }}
                        >
                          <label
                            className="ide-text-keyword"
                            style={{
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                            }}
                          >
                            USER_NAME=
                          </label>
                          <input
                            required
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleFormChange}
                            placeholder="'Enter your name'"
                            style={{
                              background: "var(--ide-bg)",
                              border: "1px solid var(--border-color)",
                              padding: "0.75rem 1rem",
                              color: "white",
                              width: "100%",
                              outline: "none",
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.5rem",
                          }}
                        >
                          <label
                            className="ide-text-keyword"
                            style={{
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                            }}
                          >
                            USER_EMAIL=
                          </label>
                          <input
                            required
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleFormChange}
                            placeholder="'your@email.com'"
                            style={{
                              background: "var(--ide-bg)",
                              border: "1px solid var(--border-color)",
                              padding: "0.75rem 1rem",
                              color: "white",
                              width: "100%",
                              outline: "none",
                            }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                        }}
                      >
                        <label
                          className="ide-text-keyword"
                          style={{
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                          }}
                        >
                          MESSAGE_BODY=
                        </label>
                        <textarea
                          required
                          name="message"
                          value={formData.message}
                          onChange={handleFormChange}
                          rows="6"
                          placeholder="'Type your message here...'"
                          style={{
                            background: "var(--ide-bg)",
                            border: "1px solid var(--border-color)",
                            padding: "0.75rem 1rem",
                            color: "white",
                            width: "100%",
                            outline: "none",
                          }}
                        ></textarea>
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          background: isSubmitting
                            ? "transparent"
                            : "var(--accent)",
                          color: isSubmitting ? "var(--accent)" : "#0d1117",
                          padding: "1rem 2.5rem",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          width: "max-content",
                          border: isSubmitting
                            ? "1px solid var(--accent)"
                            : "none",
                          cursor: isSubmitting ? "not-allowed" : "pointer",
                          opacity: isSubmitting ? 0.7 : 1,
                        }}
                      >
                        {isSubmitting ? "EXECUTING..." : "RUN post_message.sh"}{" "}
                        <Terminal size={16} />
                      </button>
                    </form>
                  ) : (
                    <div
                      className="ide-font-mono"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "1rem",
                        padding: "3rem",
                        border: "1px dashed var(--accent)",
                        background: "rgba(247, 202, 85, 0.05)",
                      }}
                    >
                      <CheckCircle size={48} color="var(--accent)" />
                      <div
                        style={{
                          color: "white",
                          fontSize: "1.25rem",
                          fontWeight: "bold",
                        }}
                      >
                        [SUCCESS] payload_delivered
                      </div>
                      <div className="ide-text-muted">
                        I'll get back to you across the signals soon.
                      </div>
                      <button
                        onClick={() => setSubmitted(false)}
                        style={{
                          marginTop: "1rem",
                          background: "transparent",
                          border: "1px solid var(--border-color)",
                          color: "white",
                          padding: "0.5rem 1.5rem",
                          cursor: "pointer",
                        }}
                      >
                        clear_terminal()
                      </button>
                    </div>
                  )}
                </div>
              </section>

              {/* Fake Footer inside scroll pane */}
              <footer
                style={{
                  padding: "2rem",
                  borderTop: "1px solid var(--border-color)",
                  textAlign: "center",
                  fontSize: "0.75rem",
                }}
                className="ide-font-mono ide-text-muted"
              >
                <div>
                  © {new Date().getFullYear()} AJO SAM | status:
                  available_for_work
                </div>
              </footer>
            </div>
          ) : (
            <div
              className="ide-editor-pane"
              style={{
                backgroundColor: "#1d2127",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <iframe
                src={resumePdf}
                style={{
                  width: "100%",
                  height: "100%",
                  flex: 1,
                  border: "none",
                }}
                title="Resume PDF"
              />
            </div>
          )}
        </main>
      </div>

      {/* Status Bar (Footer) */}
      <footer className="ide-status-bar">
        <div className="ide-flex" style={{ gap: "1rem" }}>
          <div
            className="ide-flex"
            style={{ alignItems: "center", gap: "0.25rem" }}
          >
            <GitBranch size={14} />
            <span>main*</span>
          </div>
          <div
            className="ide-flex"
            style={{ alignItems: "center", gap: "0.25rem" }}
          >
            <RefreshCw size={14} />
            <span>0 ↓ 0 ↑</span>
          </div>
        </div>
        <div className="ide-flex" style={{ gap: "1rem", alignItems: "center" }}>
          <span>Spaces: 2</span>
          <span>UTF-8</span>
          <div
            className="ide-flex"
            style={{ alignItems: "center", gap: "0.25rem" }}
          >
            <CheckCircle size={14} />
            <span>Prettier</span>
          </div>
          <Bell size={14} />
        </div>
      </footer>

      {/* <ChatWidget /> */}
    </div>
  );
};

export default ConsolePortfolio;
